import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  CreditCard, 
  Shield, 
  Calendar, 
  MessageSquare, 
  TrendingUp, 
  Star,
  CheckCircle,
  Zap,
  Globe
} from "lucide-react";

export function ExpertMarketplaceGuide() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          How the Expert Marketplace Works
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Join our trusted network of care placement experts and help families navigate complex decisions while earning competitive rates.
        </p>
      </div>

      {/* Commission Structure */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <CreditCard className="w-5 h-5" />
            Commission Structure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="secondary" className="bg-green-100 text-green-800">
                  80% to Expert
                </Badge>
                <span className="text-sm text-gray-600">You keep</span>
              </div>
              <p className="text-sm text-gray-700 mb-4">
                You set your own rates and receive 80% of every booking. For example, if you charge €150 for a consultation, you receive €120.
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                  20% Platform Fee
                </Badge>
                <span className="text-sm text-gray-600">Covers our services</span>
              </div>
              <p className="text-sm text-gray-700 mb-4">
                Our 20% fee covers client matching, secure payments, platform maintenance, and ongoing support.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* What We Provide */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-800">
            <Shield className="w-5 h-5" />
            What We Provide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-800">Client Matching</h4>
                <p className="text-sm text-green-700">We match you with families who need your specific expertise and availability.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-800">Secure Payments</h4>
                <p className="text-sm text-green-700">All payments are processed securely through our platform with automatic invoicing.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-800">Booking Management</h4>
                <p className="text-sm text-green-700">Integrated calendar system for easy scheduling and appointment management.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MessageSquare className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-800">Support</h4>
                <p className="text-sm text-green-700">Dedicated support team to help with technical issues, client management, and platform questions.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-800">Professional Profile</h4>
                <p className="text-sm text-green-700">Showcase your expertise with a professional profile visible to potential clients.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-800">Growth Support</h4>
                <p className="text-sm text-green-700">Marketing support and tools to help you grow your client base.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card className="border-purple-200 bg-purple-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <Zap className="w-5 h-5" />
            How It Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-purple-800">1</span>
              </div>
              <div>
                <h4 className="font-medium text-purple-800">Apply & Get Approved</h4>
                <p className="text-sm text-purple-700">Submit your application with your expertise, certifications, and pricing. We review and approve qualified experts within 3-5 business days.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-purple-800">2</span>
              </div>
              <div>
                <h4 className="font-medium text-purple-800">Set Up Your Profile</h4>
                <p className="text-sm text-purple-700">Create your professional profile with your bio, specializations, availability, and service packages with your own pricing.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-purple-800">3</span>
              </div>
              <div>
                <h4 className="font-medium text-purple-800">Receive Client Requests</h4>
                <p className="text-sm text-purple-700">Families will find your profile and book consultations based on your expertise, availability, and pricing.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-purple-800">4</span>
              </div>
              <div>
                <h4 className="font-medium text-purple-800">Conduct Consultations</h4>
                <p className="text-sm text-purple-700">Meet with clients via video call or phone, provide expert guidance, and help them navigate their care decisions.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-purple-800">5</span>
              </div>
              <div>
                <h4 className="font-medium text-purple-800">Get Paid</h4>
                <p className="text-sm text-purple-700">Receive 80% of your consultation fee directly to your account within 3-5 business days after the consultation.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Benefits */}
      <Card className="border-orange-200 bg-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-800">
            <Star className="w-5 h-5" />
            Benefits of Joining
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800">Set Your Own Rates</h4>
                <p className="text-sm text-orange-700">You control your pricing and can adjust based on your expertise and market demand.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800">Flexible Schedule</h4>
                <p className="text-sm text-orange-700">Choose your availability and work around your existing commitments.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800">No Marketing Needed</h4>
                <p className="text-sm text-orange-700">We handle client acquisition and marketing - you focus on providing expert guidance.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800">Professional Support</h4>
                <p className="text-sm text-orange-700">Access to our team for technical support, client management, and growth advice.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800">Secure Payments</h4>
                <p className="text-sm text-orange-700">All payments are processed securely with automatic invoicing and tax documentation.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800">Build Your Reputation</h4>
                <p className="text-sm text-orange-700">Gain reviews and build your professional reputation in the care placement space.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Commission Example */}
      <Card className="border-indigo-200 bg-indigo-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-indigo-800">
            <CreditCard className="w-5 h-5" />
            Commission Example
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-white rounded-lg p-4 border border-indigo-200">
            <h4 className="font-medium text-indigo-800 mb-3">Standard Support Package Example</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Your Consultation Fee:</span>
                <span className="font-medium">€200</span>
              </div>
              <div className="flex justify-between text-indigo-600">
                <span>Platform Fee (20%):</span>
                <span>-€40</span>
              </div>
              <div className="border-t pt-2 flex justify-between font-medium text-indigo-800">
                <span>You Receive:</span>
                <span>€160</span>
              </div>
            </div>
            <p className="text-xs text-indigo-600 mt-3">
              * Payments are processed within 3-5 business days after consultation completion
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 